import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Prompt } from "react-router";
import { Button, Card, InputNumber, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { isInteger, isNil, reject } from "lodash";
import { message, Modal } from "components";
import { LEAVE_CONFIRMATION } from "constants/common";
import useAppAuth0 from "hooks/useAppAuth0";
import { AiccSystemSettings } from "types/model/aiccCustomer";
import * as aiccAPI from "services/aiccCustomer";
import { PAGE_DESCRIPTION, UPDATE_CONFIRMATION, USAGE_LIMIT_ROWS, UsageLimitRow } from "./constants";
import styles from "./SetUsageLimits.module.scss";

function SetUsageLimits() {
  const { user } = useAppAuth0();
  const [settings, setSettings] = useState<AiccSystemSettings>();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingKey, setEditingKey] = useState<keyof AiccSystemSettings>();
  const [draftValue, setDraftValue] = useState<number>();

  const isDraftValid = !isNil(draftValue) && isInteger(draftValue) && draftValue >= 1;

  useEffect(() => {
    setIsLoading(true);
    aiccAPI
      .fetchAiccSystemSettings()
      .then(setSettings)
      .finally(() => setIsLoading(false));
  }, []);

  const exitEditing = useCallback(() => {
    setEditingKey(undefined);
    setDraftValue(undefined);
  }, []);

  const startEditing = useCallback(
    (key: keyof AiccSystemSettings) => {
      setEditingKey(key);
      setDraftValue(settings?.[key]);
    },
    [settings]
  );

  const saveLimit = useCallback(
    (key: keyof AiccSystemSettings) => {
      if (!settings || !isDraftValid) {
        return;
      }

      Modal.confirm({
        title: "Confirm",
        content: <div className={styles.confirmation}>{UPDATE_CONFIRMATION}</div>,
        okText: "Confirm Update",
        cancelText: "Cancel",
        onOk: () => {
          setIsSaving(true);
          return aiccAPI
            .updateAiccSystemSettings({ ...settings, [key]: draftValue })
            .then((response) => {
              setSettings(response);
              exitEditing();
              message.success("Usage limit updated successfully.").then();
            })
            .finally(() => setIsSaving(false));
        },
      });
    },
    [settings, draftValue, isDraftValid, exitEditing]
  );

  const columns = useMemo<ColumnsType<UsageLimitRow>>(() => {
    const allColumns: ColumnsType<UsageLimitRow> = [
      {
        title: "User Type",
        dataIndex: "userType",
        key: "userType",
        width: 200,
      },
      {
        title: "Usage Limit",
        dataIndex: "key",
        key: "usageLimit",
        width: 300,
        render: (_, { key, unit }) => (
          <div className={styles.limitCell}>
            {editingKey === key ? (
              <InputNumber<number>
                autoFocus
                min={1}
                precision={0}
                className={styles.limitInput}
                value={draftValue}
                disabled={isSaving}
                parser={(value) => Number(value?.replace(/\D/g, ""))}
                onChange={(value) => setDraftValue(isNil(value) ? undefined : value)}
              />
            ) : (
              settings?.[key]
            )}
            <span>{unit}</span>
          </div>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 200,
        render: (_, { key }) =>
          editingKey === key ? (
            <span className={styles.actions}>
              <Button
                type="link"
                size="small"
                loading={isSaving}
                disabled={!isDraftValid}
                onClick={() => saveLimit(key)}
              >
                Save
              </Button>
              <Button type="link" size="small" disabled={isSaving} onClick={exitEditing}>
                Cancel
              </Button>
            </span>
          ) : (
            <Button
              type="link"
              size="small"
              disabled={!isNil(editingKey) || isNil(settings)}
              onClick={() => startEditing(key)}
            >
              Edit
            </Button>
          ),
      },
    ];

    return user.canUpdateAiccUsageLimit ? allColumns : reject(allColumns, { key: "actions" });
  }, [
    editingKey,
    draftValue,
    isDraftValid,
    isSaving,
    settings,
    exitEditing,
    saveLimit,
    startEditing,
    user.canUpdateAiccUsageLimit,
  ]);

  return (
    <Card title="Usage Limits" className="page-container">
      <div className={styles.description}>{PAGE_DESCRIPTION}</div>

      <Prompt when={!isNil(editingKey) || isSaving} message={LEAVE_CONFIRMATION} />

      <Table<UsageLimitRow>
        className={styles.table}
        loading={isLoading}
        dataSource={USAGE_LIMIT_ROWS}
        columns={columns}
        rowKey={(record) => record.key}
        pagination={false}
      />
    </Card>
  );
}

export default SetUsageLimits;
